import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { productUrl } from '../utils/urls';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { Star, CheckCircle2, User, MessageSquare, Camera, ChevronLeft, ChevronRight, X, Package, Clock, MessageCircle, Gift, ExternalLink } from 'lucide-react';

// Sub-rating display config
const SUB_RATING_CONFIG = [
  { key: 'rating_quality', label: 'Qualità', icon: Package },
  { key: 'rating_communication', label: 'Comunicazione', icon: MessageCircle },
  { key: 'rating_shipping', label: 'Consegna', icon: Clock },
  { key: 'rating_packaging', label: 'Imballaggio', icon: Gift },
  { key: 'rating_value', label: 'Rapporto Q/P', icon: Star },
];

export function ReviewStars({ rating, size = 'sm' }) {
  const starSize = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-6 h-6';
  const starPx = size === 'sm' ? 16 : size === 'md' ? 20 : 24;
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const fillPercentage = Math.min(100, Math.max(0, (rating - (i - 1)) * 100));
        
        return (
          <div key={i} className="relative" style={{ width: starPx, height: starPx }}>
            {/* Background star (empty) */}
            <Star className={`${starSize} text-slate-200 absolute top-0 left-0`} />
            {/* Foreground star (filled) with clip */}
            <div 
              className="absolute top-0 left-0 overflow-hidden"
              style={{ width: `${fillPercentage}%` }}
            >
              <Star className={`${starSize} fill-amber-400 text-amber-400`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MiniStars({ rating, size = 'xs' }) {
  const starSize = size === 'xs' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div className="flex gap-px">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star 
          key={i}
          className={`${starSize} ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`}
        />
      ))}
    </div>
  );
}

export function SubRatingsDisplay({ review, compact = false }) {
  const hasSubRatings = SUB_RATING_CONFIG.some(({ key }) => review[key]);
  if (!hasSubRatings) return null;
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {SUB_RATING_CONFIG.map(({ key, label, icon: Icon }) => {
          const value = review[key];
          if (!value) return null;
          return (
            <div key={key} className="flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
              <Icon className="w-3 h-3" />
              <span>{label}:</span>
              <span className="font-medium text-slate-700">{value}/5</span>
            </div>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3 p-3 bg-slate-50 rounded-lg">
      {SUB_RATING_CONFIG.map(({ key, label, icon: Icon }) => {
        const value = review[key];
        if (!value) return null;
        return (
          <div key={key} className="text-center">
            <div className="flex items-center justify-center gap-1 text-slate-500 mb-1">
              <Icon className="w-3 h-3" />
              <span className="text-xs">{label}</span>
            </div>
            <MiniStars rating={value} />
          </div>
        );
      })}
    </div>
  );
}

export function ReviewSummary({ averageRating, reviewCount, size = 'sm' }) {
  if (reviewCount === 0) return null;
  
  return (
    <div className="flex items-center gap-2">
      <ReviewStars rating={averageRating} size={size} />
      <span className={`font-semibold ${size === 'sm' ? 'text-sm' : 'text-base'}`}>
        {averageRating?.toFixed(1) || '0.0'}
      </span>
      <span className={`text-slate-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
        ({reviewCount} recension{reviewCount === 1 ? 'e' : 'i'})
      </span>
    </div>
  );
}

export function ReviewImages({ images, onImageClick }) {
  if (!images || images.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {images.map((url, index) => (
        <button
          key={index}
          onClick={() => onImageClick && onImageClick(url, index)}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-slate-200 hover:border-[#008080] transition-colors"
        >
          <img
            src={url}
            alt={`Foto cliente ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </button>
      ))}
    </div>
  );
}

export function ReviewImagesCarousel({ reviews }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Collect all images from all reviews
  const allImages = reviews.reduce((acc, review) => {
    if (review.images && review.images.length > 0) {
      review.images.forEach((url, idx) => {
        acc.push({
          url,
          reviewId: review.id,
          clientName: review.client?.full_name || 'Cliente',
          rating: review.rating,
        });
      });
    }
    return acc;
  }, []);
  
  if (allImages.length === 0) return null;
  
  const openImage = (index) => {
    setCurrentIndex(index);
    setSelectedImage(allImages[index]);
  };
  
  const nextImage = () => {
    const newIndex = (currentIndex + 1) % allImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };
  
  const prevImage = () => {
    const newIndex = (currentIndex - 1 + allImages.length) % allImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  };
  
  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Foto dai clienti</h3>
          <span className="text-sm text-slate-500">({allImages.length})</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {allImages.slice(0, 10).map((img, index) => (
            <button
              key={`${img.reviewId}-${index}`}
              onClick={() => openImage(index)}
              className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border-2 border-transparent hover:border-[#008080] transition-colors"
            >
              <img
                src={img.url}
                alt={`Foto da ${img.clientName}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
          {allImages.length > 10 && (
            <button
              onClick={() => openImage(10)}
              className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
            >
              <span className="text-sm font-medium text-slate-600">+{allImages.length - 10}</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="sm:max-w-3xl p-0 bg-black/95">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Navigation arrows */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            
            {/* Image */}
            <div className="flex items-center justify-center min-h-[400px] p-8">
              <img
                src={selectedImage?.url}
                alt="Foto recensione"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
            
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{selectedImage?.clientName}</span>
                  <MiniStars rating={selectedImage?.rating} />
                </div>
                <span className="text-sm text-white/70">
                  {currentIndex + 1} / {allImages.length}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReviewCard({ review, showSubRatings = true, showProduct = true }) {
  const [lightboxImage, setLightboxImage] = useState(null);
  
  return (
    <>
      <Card className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              {review.client?.avatar_url ? (
                <img 
                  src={review.client.avatar_url} 
                  alt="" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-5 h-5 text-slate-400" />
              )}
            </div>
            <div>
              <p className="font-medium text-sm">
                {review.client?.full_name || 'Cliente verificato'}
              </p>
              <div className="flex items-center gap-2">
                <ReviewStars rating={review.rating} size="sm" />
                {review.is_verified_purchase && (
                  <Badge variant="outline" className="text-xs py-0 text-green-600 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Acquisto verificato
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <span className="text-xs text-slate-400">
            {new Date(review.created_at).toLocaleDateString('it-IT')}
          </span>
        </div>

        {review.title && (
          <h4 className="font-semibold mb-1">{review.title}</h4>
        )}
        
        {review.comment && (
          <p className="text-sm text-slate-600 mb-3">{review.comment}</p>
        )}

        {/* Product Info - only show if showProduct is true */}
        {showProduct && review.product && (
          <a 
            href={productUrl(review.product)}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-3 flex items-center gap-3 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
          >
            {/* Product Thumbnail */}
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
              {review.product.images?.[0] ? (
                <img 
                  src={review.product.images[0]} 
                  alt={review.product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-4 h-4 text-slate-300" />
                </div>
              )}
            </div>
            {/* Product Title */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate group-hover:text-[#008080] transition-colors">
                {review.product.title}
              </p>
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Vedi prodotto
              </p>
            </div>
          </a>
        )}
        
        {/* Sub-ratings */}
        {showSubRatings && <SubRatingsDisplay review={review} compact />}
        
        {/* Review Images */}
        {review.images && review.images.length > 0 && (
          <ReviewImages 
            images={review.images} 
            onImageClick={(url, index) => setLightboxImage({ url, index })}
          />
        )}

        {/* Crafter Response */}
        {review.crafter_response && (
          <div className="mt-3 pl-4 border-l-2 border-[#008080]/30 bg-[#008080]/5 rounded-r-lg p-3">
            <p className="text-xs font-medium text-[#008080] mb-1 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              Risposta dell'artigiano
            </p>
            <p className="text-sm text-slate-600">{review.crafter_response}</p>
            {review.crafter_response_at && (
              <p className="text-xs text-slate-400 mt-1">
                {new Date(review.crafter_response_at).toLocaleDateString('it-IT')}
              </p>
            )}
          </div>
        )}
      </Card>
      
      {/* Single image lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="sm:max-w-2xl p-0 bg-black/95">
          <div className="relative">
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center min-h-[300px] p-8">
              <img
                src={lightboxImage?.url}
                alt="Foto recensione"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReviewsList({ crafterId, productId, limit = 5, showCarousel = true }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ average: 0, count: 0, distribution: {}, subAverages: {} });

  useEffect(() => {
    loadReviews();
  }, [crafterId, productId]);

  const loadReviews = async () => {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          client:client_id(id, full_name, avatar_url),
          product:product_id(id, title, images)
        `)
        .order('created_at', { ascending: false });

      if (productId) {
        query = query.eq('product_id', productId);
      } else if (crafterId) {
        query = query.eq('crafter_id', crafterId);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      setReviews(data || []);

      // Calculate stats including sub-ratings averages
      if (data && data.length > 0) {
        const total = data.reduce((sum, r) => sum + r.rating, 0);
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        data.forEach(r => distribution[r.rating]++);
        
        // Calculate sub-rating averages
        const subAverages = {};
        SUB_RATING_CONFIG.forEach(({ key }) => {
          const values = data.filter(r => r[key]).map(r => r[key]);
          if (values.length > 0) {
            subAverages[key] = values.reduce((a, b) => a + b, 0) / values.length;
          }
        });
        
        setStats({
          average: total / data.length,
          count: data.length,
          distribution,
          subAverages
        });
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-slate-100 rounded-lg" />
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <Card className="p-6 text-center border-dashed">
        <Star className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500">Ancora nessuna recensione</p>
        <p className="text-sm text-slate-400">Le recensioni appariranno qui dopo i primi ordini completati</p>
      </Card>
    );
  }

  const hasSubAverages = Object.keys(stats.subAverages).length > 0;

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="p-4 bg-slate-50 rounded-xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="text-center sm:text-left">
            <p className="text-3xl font-bold text-slate-800">{stats.average.toFixed(1)}</p>
            <ReviewStars rating={stats.average} size="md" />
            <p className="text-xs text-slate-500 mt-1">{stats.count} recension{stats.count === 1 ? 'e' : 'i'}</p>
          </div>
          
          <div className="flex-1 space-y-1 w-full sm:w-auto">
            {[5, 4, 3, 2, 1].map(star => {
              const count = stats.distribution[star] || 0;
              const percentage = stats.count > 0 ? (count / stats.count) * 100 : 0;
              
              return (
                <div key={star} className="flex items-center gap-2 text-xs">
                  <span className="w-3">{star}</span>
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-slate-500">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Sub-rating averages */}
        {hasSubAverages && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs font-medium text-slate-500 mb-2">Valutazioni dettagliate</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {SUB_RATING_CONFIG.map(({ key, label, icon: Icon }) => {
                const avg = stats.subAverages[key];
                if (!avg) return null;
                return (
                  <div key={key} className="text-center p-2 bg-white rounded-lg">
                    <Icon className="w-4 h-4 mx-auto text-slate-400 mb-1" />
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="font-semibold text-slate-800">{avg.toFixed(1)}</p>
                    <MiniStars rating={Math.round(avg)} size="xs" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Images Carousel */}
      {showCarousel && <ReviewImagesCarousel reviews={reviews} />}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.map(review => (
          <ReviewCard key={review.id} review={review} showProduct={!productId} />
        ))}
      </div>
    </div>
  );
}

export default ReviewsList;
